import { useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Button, Icon, Loader, Message } from 'semantic-ui-react';
import Swal from 'sweetalert2';
import EventService from 'src/services/api/eventService';
import { chPerm, churchServices, dayInTZ, eventDisplayName } from 'src/components/helpers';
import moment from 'src/services/moment_instance';
import useServiceForm from './hooks/useServiceForm';
import BasicDataSection from './sections/BasicDataSection';
import PaymentSection from './sections/PaymentSection';
import NotificationsSection from './sections/NotificationsSection';
import ExtraQuestionsSection from './sections/ExtraQuestionsSection';
import EventOptionsSection from './sections/EventOptionsSection';
import ContentSection from './sections/ContentSection';
import {
  ActionBar,
  FormLayout,
  PageHeader,
  SectionNav,
  ServiceFormPageContainer,
} from './serviceFormStyles';
import { getEffectiveSlugname, serviceToFormInputs, SERVICES_PATH } from './serviceFormUtils';

const SECTIONS = [
  { id: 'datos-basicos', title: 'Datos básicos' },
  { id: 'pago', title: 'Pago' },
  { id: 'notificaciones', title: 'Notificaciones' },
  { id: 'preguntas-extra', title: 'Preguntas extra' },
  { id: 'opciones', title: 'Opciones' },
  { id: 'contenido', title: 'Contenido' },
];

const scrollIntoView = (element) =>
  element?.scrollIntoView({ behavior: 'smooth', block: 'center' });

const fetchService = async (eventId, date) => {
  const range = date
    ? { startDate: date, endDate: date }
    : {
        startDate: moment().subtract(2, 'years').toDate(),
        endDate: moment().add(2, 'years').toDate(),
      };
  const response = await EventService.getAuditoriumEvents(range);
  return response.data.events?.find((event) => String(event.id) === String(eventId));
};

const ServiceForm = ({ eventId, initialInputs, eventNames, onCancel, onSaved }) => {
  const form = useServiceForm({ initialInputs, eventId });
  const { loading, hasErrors, submit } = form;
  const sectionProps = { ...form, eventNames };

  const handleSubmit = async () => {
    const result = await submit();
    if (result) {
      onSaved(result);
      return;
    }
    requestAnimationFrame(() =>
      scrollIntoView(document.querySelector('.service-form .field__error, .question__error'))
    );
  };

  return (
    <>
      <FormLayout className="service-form">
        <SectionNav aria-label="Secciones del formulario">
          {SECTIONS.map((section, index) => (
            <button
              key={section.id}
              type="button"
              onClick={() =>
                document
                  .getElementById(section.id)
                  ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
              }
            >
              <span>{index + 1}</span>
              {section.title}
            </button>
          ))}
        </SectionNav>
        <div>
          <BasicDataSection {...sectionProps} />
          <PaymentSection {...sectionProps} />
          <NotificationsSection {...sectionProps} />
          <ExtraQuestionsSection {...sectionProps} />
          <EventOptionsSection {...sectionProps} />
          <ContentSection {...sectionProps} />
        </div>
      </FormLayout>
      <ActionBar>
        <div className="action-bar__inner">
          {hasErrors && <p className="action-bar__error">Revisa los campos marcados en rojo.</p>}
          <Button type="button" disabled={loading} onClick={onCancel}>
            Cancelar
          </Button>
          <Button type="button" primary loading={loading} disabled={loading} onClick={handleSubmit}>
            {eventId ? 'Guardar cambios' : 'Crear evento'}
          </Button>
        </div>
      </ActionBar>
    </>
  );
};

const ServiceFormPage = () => {
  const { eventId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const isEdit = Boolean(eventId);
  const [eventNames, set_eventNames] = useState(null);
  const [initialInputs, set_initialInputs] = useState(() =>
    isEdit ? null : { eventSlugname: location.state?.eventSlugname }
  );
  const [error, set_error] = useState(null);

  useEffect(() => {
    EventService.getEventsNames({ prevDays: 9999, postDays: 9999 })
      .then((response) => {
        const names = response.data.success ? response.data.eventsNames : [];
        set_eventNames(names.length ? names : churchServices);
      })
      .catch(() => set_eventNames(churchServices));
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    const stateService = location.state?.service;
    if (String(stateService?.id) === String(eventId)) {
      set_initialInputs(serviceToFormInputs(stateService));
      return;
    }
    const date = new URLSearchParams(location.search).get('date');
    fetchService(eventId, date)
      .then((service) => {
        if (service) set_initialInputs(serviceToFormInputs(service));
        else set_error('No encontramos el evento que quieres modificar.');
      })
      .catch(() => set_error('Hubo un error cargando el evento.'));
  }, [eventId, isEdit, location.state, location.search]);

  const goToList = (state) => history.push(SERVICES_PATH, state);

  const listStateFor = (inputs) => ({
    eventSlugname: getEffectiveSlugname(inputs) || undefined,
    date: inputs?.date ? moment(inputs.date).format('YYYY-MM-DD') : undefined,
  });

  const handleSaved = ({ body, message }) => {
    if (message) Swal.fire(message);
    goToList(listStateFor(body));
  };

  const handleCancel = () => goToList(initialInputs ? listStateFor(initialInputs) : undefined);

  const renderBody = () => {
    if (!chPerm(['Auditorio_PrincipalAdmin'])) {
      return (
        <Message warning>
          <Message.Header>Sin permisos</Message.Header>
          <p>No tienes permisos para crear o modificar eventos.</p>
        </Message>
      );
    }
    if (error) {
      return (
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      );
    }
    if (!eventNames || !initialInputs) {
      return (
        <Loader active inline="centered">
          Cargando...
        </Loader>
      );
    }
    return (
      <ServiceForm
        eventId={eventId}
        initialInputs={initialInputs}
        eventNames={eventNames}
        onCancel={handleCancel}
        onSaved={handleSaved}
      />
    );
  };

  const editSubtitle =
    isEdit && initialInputs
      ? `${eventDisplayName(getEffectiveSlugname(initialInputs))} · ${dayInTZ(
          initialInputs.date,
          'dddd DD [de] MMMM [de] YYYY, hh:mm a'
        )}`
      : null;

  return (
    <ServiceFormPageContainer>
      <div className="service-form__inner">
        <PageHeader>
          <button type="button" className="back-link" onClick={handleCancel}>
            <Icon name="arrow left" />
            Volver a servicios y eventos
          </button>
          <h1>{isEdit ? 'Modificar evento' : 'Crear evento'}</h1>
          <p>
            {editSubtitle ||
              'Completa la información por secciones. Solo los datos básicos son obligatorios.'}
          </p>
        </PageHeader>
        {renderBody()}
      </div>
    </ServiceFormPageContainer>
  );
};

export default ServiceFormPage;
